<script setup>
// ============================================================================
// BonBoZ: trang Thống kê > ĐÁNH GIÁ — dựng theo đúng bố cục Pancake.
//
// BỐ CỤC:
//   1. "Đánh giá" — vòng tròn, chữ giữa "Trung bình đánh giá chung / 0.0 ★" +
//      huy hiệu chênh lệch, ba dòng chú giải có chấm màu và số bên phải
//   2. "Top nhân viên" — các nhân viên có nhiều đánh giá nhất
//   3. "Thống kê chi tiết đánh giá" — tab [Đánh giá theo nhân viên]
//      [Chi tiết đánh giá] + bộ lọc nhân viên
//
// NGUỒN LÀ CSAT CỦA CHÍNH CHATWOOT (`csat_survey_responses`), không phải bảng
// riêng. Phòng khám chưa bật khảo sát nên hôm nay là 0 — đúng như chính Pancake
// đang hiện. Bật ở Cài đặt → Hộp thư là số tự lên, không phải sửa gì ở đây.
//
// BA RỔ SAO (5 / 3–4 / 1–2) là quy ước của Pancake, khai ở máy chủ để trang này
// và thẻ Đánh giá của trang Tổng quan không thể ra hai con số khác nhau.
// ============================================================================
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import HieuSuatAPI from 'dashboard/api/bonboz/hieuSuat';
import { useAlert } from 'dashboard/composables';
import { useSelectedPages } from 'dashboard/composables/useSelectedPages';
import { mauCss } from 'dashboard/helper/mauBieuDo';
import BieuDoTron from './BieuDoTron.vue';
import BangDanhGia from './BangDanhGia.vue';
import SectionCard from './SectionCard.vue';
import ReportHeader from '../components/ReportHeader.vue';
import OverviewReportFilters from '../components/OverviewReportFilters.vue';
import PhamVi from './PhamVi.vue';

const { t } = useI18n();
// Moi con so tren trang nay chi duoc dem tren nhung trang dang chon o bang
// dieu khien.
const { selectedInboxIds } = useSelectedPages();

const dangTai = ref(true);
const khoang = ref(null);
const soLieu = ref(null);

const tai = async () => {
  if (!khoang.value) return;
  dangTai.value = true;
  try {
    const { data } = await HieuSuatAPI.danhGia({
      since: khoang.value.from,
      until: khoang.value.to,
      inboxIds: selectedInboxIds.value,
    });
    soLieu.value = data;
  } catch {
    soLieu.value = null;
    useAlert(t('BONBOZ.DANH_GIA.LOI_TAI'));
  } finally {
    dangTai.value = false;
  }
};

const doiBoLoc = ({ from, to }) => {
  if (!from || !to) return;
  khoang.value = { from, to };
  tai();
};

// Lua chon trang nam trong `ui_settings` cua nguoi dung, tai ve SAU khi trang
// da mount — lan tai dau tien co the chay khi mang con rong (= tat ca).
watch(selectedInboxIds, tai);

// ------------------------------------------------------------------ số liệu
const tong = computed(
  () =>
    soLieu.value?.tong || {
      so_luot: 0,
      trung_binh: 0,
      thay_doi: null,
      tot: 0,
      kha: 0,
      kem: 0,
    }
);
const nhanVien = computed(() => soLieu.value?.nhan_vien || []);

const DONG_SAO = [
  { khoa: 'tot', nhan: 'BONBOZ.TONG_QUAN.SAO_TOT', bien: '--bonboz-xong' },
  { khoa: 'kha', nhan: 'BONBOZ.TONG_QUAN.SAO_KHA', bien: '--bonboz-cho' },
  { khoa: 'kem', nhan: 'BONBOZ.TONG_QUAN.SAO_KEM', bien: '--bonboz-tre' },
];

const dongSao = computed(() =>
  DONG_SAO.map(d => ({ ...d, so: tong.value[d.khoa] }))
);

// `mauCss` đọc biến CSS ra chuỗi `rgb(...)`: canvas KHÔNG hiểu `var(--x)`, đặt
// thẳng vào là miếng bánh ra đen. Xem đầu `helper/mauBieuDo.js`.
const miengTron = computed(() => {
  if (!tong.value.so_luot) {
    // Chưa có đánh giá nào thì vẫn phải vẽ một vòng xám: canvas trống trông
    // như biểu đồ hỏng, còn vòng xám đọc ra ngay là "kỳ này chưa có gì".
    return [{ tag: '', so: 1, nen: mauCss('--bonboz-mo', 0.12) }];
  }
  return dongSao.value.map(d => ({
    tag: t(d.nhan),
    so: d.so,
    nen: mauCss(d.bien),
  }));
});

// Mũi tên là HÌNH chứ không phải màu, nên người không phân biệt được màu vẫn
// đọc được. `null` = chưa có kỳ trước để so, lúc đó im lặng thay vì hiện 0%.
const nhip = computed(() => {
  const n = tong.value.thay_doi;
  if (n === null || n === undefined) return null;
  if (n > 0) return { mui: '▲', huong: 'text-n-teal-11 bg-n-teal-3' };
  if (n < 0) return { mui: '▼', huong: 'text-n-ruby-11 bg-n-ruby-3' };
  return { mui: '', huong: 'text-n-slate-11 bg-n-alpha-1' };
});

// Top nhân viên: NHIỀU ĐÁNH GIÁ NHẤT, không phải điểm cao nhất — đúng như phụ
// đề Pancake. Người được ba khách chấm 5 sao chưa hẳn hơn người được ba mươi
// khách chấm 4,5 sao.
const topNhat = computed(() => nhanVien.value.slice(0, 5));
const caoNhat = computed(() =>
  Math.max(1, ...topNhat.value.map(n => n.so_luot))
);
</script>

<template>
  <ReportHeader
    :header-title="$t('BONBOZ.DANH_GIA.TIEU_DE')"
    :header-description="$t('BONBOZ.DANH_GIA.MO_TA')"
  />

  <div class="flex flex-col w-full gap-4 mx-auto max-w-6xl">
    <OverviewReportFilters :disabled="dangTai" @filter-change="doiBoLoc" />

    <PhamVi />

    <p
      v-if="!dangTai && !tong.so_luot"
      class="px-3 py-2 m-0 text-xs rounded-lg text-n-amber-11 bg-n-amber-3"
    >
      {{ $t('BONBOZ.DANH_GIA.CHUA_BAT') }}
    </p>

    <div class="grid gap-4 lg:grid-cols-2 lg:items-start">
      <!-- ============ 1. ĐÁNH GIÁ ============ -->
      <SectionCard
        :title="$t('BONBOZ.DANH_GIA.TIEU_DE')"
        :description="$t('BONBOZ.DANH_GIA.TONG_QUAN')"
      >
        <div
          v-if="dangTai"
          class="h-64 rounded-lg bg-n-alpha-2 animate-pulse"
        />
        <template v-else>
          <div class="relative mx-auto size-52">
            <div class="absolute inset-0">
              <BieuDoTron :dong="miengTron" />
            </div>
            <!-- Chữ giữa là HTML ĐÈ lên canvas — dịch và style dễ hơn plugin
                 vẽ chữ của chart.js nhiều. -->
            <div
              class="absolute inset-0 grid px-10 text-center pointer-events-none place-content-center"
            >
              <span class="text-sm leading-5 text-n-slate-11">
                {{ $t('BONBOZ.TONG_QUAN.DIEM_TRUNG_BINH') }}
              </span>
              <span
                class="flex items-center justify-center gap-1 mt-1 text-3xl font-semibold tabular-nums text-n-slate-12"
              >
                {{ tong.trung_binh.toFixed(1) }}
                <span
                  class="inline-block i-lucide-star size-5 text-n-amber-11"
                  aria-hidden="true"
                />
              </span>
              <span
                v-if="nhip"
                class="justify-self-center mt-1.5 px-1.5 py-0.5 text-xs rounded-full tabular-nums pointer-events-auto"
                :class="nhip.huong"
              >
                <span aria-hidden="true">{{ nhip.mui }}</span>
                {{ Math.abs(tong.thay_doi) }}%
              </span>
            </div>
          </div>

          <ul class="flex flex-col gap-3 p-0 m-0 mt-6 list-none">
            <li
              v-for="d in dongSao"
              :key="d.khoa"
              class="flex items-center gap-2"
            >
              <span
                class="rounded-full size-3 shrink-0"
                :style="{ background: `rgb(var(${d.bien}))` }"
                aria-hidden="true"
              />
              <span class="flex-1 text-sm truncate text-n-slate-11">
                {{ $t(d.nhan) }}
              </span>
              <span class="text-sm tabular-nums text-n-slate-12">
                {{ d.so }}
              </span>
            </li>
          </ul>
        </template>
      </SectionCard>

      <!-- ============ 2. TOP NHÂN VIÊN ============ -->
      <SectionCard
        :title="$t('BONBOZ.DANH_GIA.TOP_NHAN_VIEN')"
        :description="$t('BONBOZ.DANH_GIA.TOP_MO_TA')"
      >
        <div
          v-if="dangTai"
          class="h-64 rounded-lg bg-n-alpha-2 animate-pulse"
        />
        <p
          v-else-if="!topNhat.length"
          class="m-0 py-24 text-sm text-center text-n-slate-11"
        >
          {{ $t('BONBOZ.DANH_GIA.CHUA_CO') }}
        </p>
        <ul v-else class="flex flex-col gap-4 p-0 m-0 list-none">
          <li v-for="n in topNhat" :key="n.id" class="flex items-center gap-2">
            <span class="w-24 text-sm truncate shrink-0 text-n-slate-12">
              {{ n.ten }}
            </span>
            <span class="flex items-center flex-1 gap-2 min-w-0">
              <span
                class="h-7 rounded bg-[rgb(var(--bonboz-mo)/0.18)]"
                :style="{
                  width: `${Math.max(4, (n.so_luot / caoNhat) * 100)}%`,
                }"
              />
              <span
                class="text-xs whitespace-nowrap tabular-nums text-n-slate-11"
              >
                {{ $t('BONBOZ.DANH_GIA.N_LUOT', { n: n.so_luot }) }}
              </span>
            </span>
          </li>
        </ul>
      </SectionCard>
    </div>

    <!-- ============ 3. BẢNG CHI TIẾT ============ -->
    <BangDanhGia
      :nhan-vien="nhanVien"
      :chi-tiet="soLieu?.chi_tiet || []"
      :tong="tong"
      :dang-tai="dangTai"
    />
  </div>
</template>
